import React, { useState } from "react";
import { Row, Col } from "react-grid-system";
import { ICustomer, ICustomerDelegation } from "dataModels/contract";
import { AppUserRole, BadgeStatus, ClientType, DATE_FORMAT, DocumentType, DsLayout, EntityType, IAppUserProfile, ResponseCode, ResponseStatus, RoutePath } from "shared/constants";
import { useTranslation } from "react-i18next";
import { Output, Table } from "@ds/ds-snam/react";
import { getDSTableSharedElements, getTableCellsHeaderSimple } from "shared/utils/dsComponentUtils";
import { getDelegationLegend } from "../shared/listUtils";
import { RootState } from "state/appStore";
import { useDispatch, useSelector } from "react-redux";
import { ITableCell, ITableConfig, ITableRow } from "@ds/ds-snam/dist/models/tables";
import { IParseDateFormatType, parseDateForDS } from "shared/utils/dateUtils";
import moment from "moment";
import useAppUser from "hooks/useAppUser";
import { AppUser } from "ooAuth2/appUser";
import { requestPending, requestPristine } from "state/reducer/requestStatusSlice";
import { deleteCustomerDelegationAPI, downloadDocument, postCustomerDelegation } from "net/contracts";
import { INotificationAction } from "dataModels/stateModels/notification";
import { hideNotification, showNotification } from "state/reducer/notificationSlice";
import { useNavigate } from "react-router";
import { saveAs } from "file-saver";
import DialogMessage from "components/DialogMessage";

interface ICustomerFormReviewProps {
    delegations: ICustomerDelegation[];
    delegationType: "carried" | "received";
}
const CustomerDelegationData: React.FC<ICustomerFormReviewProps> = (props): JSX.Element => {
    const { t } = useTranslation(['contract', 'app']);
    const layoutMode = useSelector((state: RootState) => {
        return state.dsLayout.mode;
    });
    const { delegations, delegationType } = props;
    const replaceEmpty = (value: string): string => {
        return value.length ? value : "-";
    }
    const appUser: AppUser | undefined = useAppUser();
    const appUserRole: AppUserRole | undefined = appUser?.getRole();
    const appUserProfile: IAppUserProfile | undefined = appUser?.userProfile;
    const [viewRejectionPopup, setViewRejectionPopup] =useState<boolean>(false);

const dispatch = useDispatch();
const navigate = useNavigate();
    const showDelegationNotification = (operation: "approved" | "rejected" | "deleted", response?: { status?: ResponseStatus; code?: ResponseCode }) => {
        const isError = !response || response.status === ResponseStatus.ERROR;
        const isWarning = response?.code === ResponseCode.UNPROCESSABLE_CONTENT;
        const popUpAction: INotificationAction = {
            show: true,
            title: isError
                ? t("customer.customerDelegationForm.operationNotifications.errorTitle")
                : isWarning
                    ? t("customer.customerDelegationForm.operationNotifications.warningTitle")
                    : t("customer.customerDelegationForm.operationNotifications.successTitle"),
            description: isError
                ? t("customer.customerDelegationForm.operationNotifications.errorDescription")
                : isWarning
                    ? t("customer.customerDelegationForm.operationNotifications.warningDescription")
                    : t(`customer.customerDelegationForm.operationNotifications.${operation}`),
            status: isError ? "error" : isWarning ? "warning" : "confirm",
            onClose: () => dispatch(hideNotification())
        };
        dispatch(showNotification(popUpAction));
    };
    const postDelegation = (delegation : ICustomerDelegation,message?:string) => {
        dispatch(requestPending());
        const normalizedMessage = typeof message === "string" && message.length > 0
            ? message
            : undefined;
        const postDelegationRequest = normalizedMessage
            ? postCustomerDelegation(delegation, normalizedMessage)
            : postCustomerDelegation(delegation);

        postDelegationRequest
            .then((response) => {
                showDelegationNotification(message ? "rejected" : "approved", response);
                navigate(RoutePath.CONTRACTS_CUSTOMER_INFO);  //TODO
            })
            .catch(error => {
                console.log(error);
                showDelegationNotification(message ? "rejected" : "approved");
            })
            .finally(() => {
                dispatch(requestPristine());
            })
                       
            
    }

    const approveDelegation = (row: string) => { //row 0,1,ecc
        const rowIndex = parseInt(row);
        let delegationToEdit = delegations[rowIndex];
        delegationToEdit.documents[0].status = BadgeStatus.APPROVED;
        postDelegation(delegationToEdit);
    }
    const [rejectionRow,setRejectionRow] = useState<number|undefined>(undefined);
    const rejectDelegation = (message: string) => {
        if(rejectionRow!==undefined){
            const rowIndex = rejectionRow;
            let delegationToEdit = delegations[rowIndex];
            delegationToEdit.documents[0].status = BadgeStatus.NOTAPPROVED;
            postDelegation(delegationToEdit,message);
            setViewRejectionPopup(false);
            setRejectionRow(undefined);
        }
        
    }
    const editDelegation = (row: string) => {
        const rowIndex = parseInt(row);
        let delegationToEdit = delegations[rowIndex];
        navigate(`${RoutePath.CONTRACTS_NEW_DELEGATION}/${delegationToEdit.delegationId}/${delegationToEdit.contractId}`);
    }
    const deleteDelegation = (row: string) => {
        const rowIndex = parseInt(row);
        let delegationToEdit = delegations[rowIndex];
        deleteCustomerDelegationAPI(delegationToEdit.delegationId)
            .then(response => {
                showDelegationNotification("deleted", response);
                navigate(RoutePath.CONTRACTS_CUSTOMER_INFO);
            })
            .catch(error => {
                console.log(error);
                showDelegationNotification("deleted");
            });
    }
    const downloadDelegationDocument = (row: string) => {
         const rowIndex = parseInt(row);
        let delegationToEdit = delegations[rowIndex];
        downloadDocument(delegationToEdit.delegationId, EntityType.CUSTOMERDELEGATION, DocumentType.CUSTOMER_DELEGATION)
            .then((response) => {
                if(response.data.code===ResponseCode.UNPROCESSABLE_CONTENT){
                    showPopupError()
                }
                let file = new File(
                    [response.data],
                    "customerdelegation_" + DocumentType.CUSTOMER_DELEGATION + "_" + delegationToEdit.delegationId + ".pdf"
                );
                saveAs(file);
             })
            .catch((error) => {
                showPopupError();
                console.log(error);
            });
    }
    const disableEnableDelegation = (row: string) => {
        //TODO
    }




    const getTableRowsDelegations = (): ITableRow[] => {
        let myRetArr: ITableRow[] = [];
        let test : ITableCell
        if (delegations) {
            for (let i = 0; i < delegations.length; i++) {
                    let tempObj: ITableRow = {
                        tableCells: [
                            { 
                                type: "text-badge", 
                                text: delegations[i].delegatingBusinessName,
                                badgeStatus: delegations[i].documents[0].status===BadgeStatus.EXPIRED ? "error" : 
                                delegations[i].documents[0].status===BadgeStatus.TOAPPROVE ? "warning" : 
                                delegations[i].documents[0].status===BadgeStatus.APPROVED ? "confirm" : "error",
                            },
                            { 
                                type: "text", 
                                text: delegations[i].delegatedBusinessName,
                            },
                            { 
                                type: "text", 
                                text: delegations[i].contractCode,
                            },
                            { 
                                type: "text", 
                                text: delegations[i].documents[0].expire !== null ? parseDateForDS(
                                moment
                                .unix(delegations[i].documents[0].expire as number)
                                .format(DATE_FORMAT),
                                IParseDateFormatType.DATE_FORMAT1
                                ) : ""
                                }
                        ],
                        //rightIcon: "icon-download",
                        "rightIconMenu": true,
                        "rightIconMenuData": (appUserRole===AppUserRole.ADMIN||appUserRole===AppUserRole.SALES_PROFILE) ? (
                            (delegations[i].documents[0].status === BadgeStatus.APPROVED||delegations[i].documents[0].status === BadgeStatus.NOTAPPROVED) ?
                            [
                                
                                {
                                    "actionLabel": t("general.buttons.modify",{ ns: "app" }),
                                    "actionAriaLabel": t("general.buttons.modify",{ ns: "app" }), //EDIT
                                    "actionDisabled": false,
                                },
                                {
                                    "actionLabel": t("general.buttons.removeElement",{ ns: "app", what: "" }),
                                    "actionAriaLabel": t("general.buttons.removeElement",{ ns: "app", what: "" }), // DELETE
                                    "actionDisabled": false,
                                },
                                {
                                    "actionLabel": t("general.buttons.download_short",{ ns: "app" }),
                                    "actionAriaLabel": t("general.buttons.download_short",{ ns: "app" }), //DOWNLOAD
                                    "actionDisabled": false,
                                },
                                /*{
                                    "actionLabel": t("general.buttons.disable_enable",{ ns: "app" }),
                                    "actionAriaLabel": t("general.buttons.disable_enable",{ ns: "app" }), //ABILITA/DISABILITA DELEGA
                                    "actionDisabled": false,
                                }*/
                            ]
                            :
                            [
                                {
                                    "actionLabel": t("general.buttons.approve",{ ns: "app", what: "" }), //APPROVE
                                    "actionAriaLabel": t("general.buttons.approve",{ ns: "app", what: "" }),
                                    "actionDisabled": false,
                                    //"actionIcon": "icon-share"
                                },
                                {
                                    "actionLabel": t("general.buttons.reject",{ ns: "app", what: "" }), //REJECT/NON APPROVA
                                    "actionAriaLabel": t("general.buttons.reject",{ ns: "app", what: "" }),
                                    "actionDisabled": false,
                                },
                                {
                                    "actionLabel": t("general.buttons.modify",{ ns: "app" }),
                                    "actionAriaLabel": t("general.buttons.modify",{ ns: "app" }), //EDIT
                                    "actionDisabled": false,
                                },
                                {
                                    "actionLabel": t("general.buttons.removeElement",{ ns: "app", what: "" }),
                                    "actionAriaLabel": t("general.buttons.removeElement",{ ns: "app", what: "" }), // DELETE
                                    "actionDisabled": false,
                                },
                                {
                                    "actionLabel": t("general.buttons.download_short",{ ns: "app" }),
                                    "actionAriaLabel": t("general.buttons.download_short",{ ns: "app" }), //DOWNLOAD
                                    "actionDisabled": false,
                                },
                                /*{
                                    "actionLabel": t("general.buttons.disable_enable",{ ns: "app" }),
                                    "actionAriaLabel": t("general.buttons.disable_enable",{ ns: "app" }), //ABILITA/DISABILITA DELEGA
                                    "actionDisabled": false,
                                }*/
                            ]
                        )
                        :
                        (
                            delegationType==="received" ?
                            [
                                {
                                    "actionLabel": t("general.buttons.download_short",{ ns: "app" }),
                                    "actionAriaLabel": t("general.buttons.download_short",{ ns: "app" }), //DOWNLOAD
                                    "actionDisabled": false,
                                }
                            ]
                            :
                            [
                                {
                                    "actionLabel": t("general.buttons.modify",{ ns: "app" }),
                                    "actionAriaLabel": t("general.buttons.modify",{ ns: "app" }), //EDIT
                                    "actionDisabled": false,
                                },
                                {
                                    "actionLabel": t("general.buttons.removeElement",{ ns: "app", what: "" }),
                                    "actionAriaLabel": t("general.buttons.removeElement",{ ns: "app", what: "" }), // DELETE
                                    "actionDisabled": false,
                                },
                                {
                                    "actionLabel": t("general.buttons.download_short",{ ns: "app" }),
                                    "actionAriaLabel": t("general.buttons.download_short",{ ns: "app" }), //DOWNLOAD
                                    "actionDisabled": false,
                                }
                            ]
                        )
                        
                    };
                    myRetArr.push(tempObj);           
            }
        }
        return myRetArr;
    };
    const myTableConfigDelegations: ITableConfig = {
            ...getDSTableSharedElements(),
            tableHeader: {
                tableCellsHeader: [
                    getTableCellsHeaderSimple(t("customer.customerDelegationForm.table_1") as string,"col1","25%","left"),
                    getTableCellsHeaderSimple(t("customer.customerDelegationForm.table_2") as string,"col2","25%","left"),
                    getTableCellsHeaderSimple(t("customer.customerDelegationForm.table_3") as string,"col2","25%","left"),
                    getTableCellsHeaderSimple(t("customer.customerDelegationForm.table_4") as string,"col2","25%","left"),
                    //getTableCellsHeaderSimple(t("customer.customerDelegationForm.table_5") as string,"col2","20%","left"),
                ],
                hasSearchBox: true,
            },
            tableRows: getTableRowsDelegations()
    };
    
    return (
    <>
        <div className="listTableContainer" style={{ background: "" }}>
            {delegations.length !== 0 ? (
                <>
                <p
                                    style={{
                                    fontWeight: "bold",
                                    fontSize: "90%",
                                    color:
                                        layoutMode === DsLayout.DARK_MODE
                                        ? "white"
                                        : "#0e2852",
                                }}
                                >
                                {delegationType==='carried'?t("customer.customerDelegationForm.title_3") as string:t("customer.customerDelegationForm.title_4") as string}
                                </p>{" "}
                    {
                        <Table 
                            tableConfig={myTableConfigDelegations} 
                            style={{}}
                            onColumnSorting={(event:Event)=>{
                                //console.log(event)
                            }}
                            onInputSearchBarChange={(event:Event)=>{
                                console.log((event as CustomEvent).detail.searchedText + " - " + (event as CustomEvent).detail.cells)
                            }}
                            onClickRightAction={(event:Event)=>{
                                console.log(event)
                                //navigate(contractList[(event as CustomEvent).detail.row].contractId)
                            }}
                            onButtonItemCellClick={(event:Event)=>{
                                console.log(event)
                            }}

                            /**
                             * 
                             * admin
                             * delegations[i].documents[0].status === BadgeStatus.APPROVED||delegations[i].documents[0].status === BadgeStatus.NOTAPPROVED
                                1 edit
                                2 delete
                                3 download
                                4 abilitation
                                :
                                1 approve
                                2 reject
                                3 edit
                                4 delete
                                5 download
                                6 abilitation
                               cliente
                                "carried"
                                1 edit
                                2 delete
                                3 download
                                "received"
                                3 download
                             */
        
                            onClickKebabAction1={(event:Event)=>{ 
                                if(appUserRole===AppUserRole.CUSTOMER && delegationType==="carried"){
                                    //edit
                                    editDelegation((event as CustomEvent).detail.row);
                                }else if (appUserRole===AppUserRole.CUSTOMER && delegationType==="received"){
                                    //download
                                    downloadDelegationDocument((event as CustomEvent).detail.row);
                                }else if (appUserRole===AppUserRole.ADMIN || appUserRole===AppUserRole.SALES_PROFILE){
                                    if(delegations[parseInt((event as CustomEvent).detail.row)].documents[0].status === BadgeStatus.APPROVED||delegations[parseInt((event as CustomEvent).detail.row)].documents[0].status === BadgeStatus.NOTAPPROVED){
                                        editDelegation((event as CustomEvent).detail.row);
                                    }else{
                                        approveDelegation((event as CustomEvent).detail.row);
                                    }
                                    
                                }
                            }}
                            onClickKebabAction2={(event:Event)=>{ 
                               if(appUserRole===AppUserRole.CUSTOMER && delegationType==="carried"){
                                    deleteDelegation((event as CustomEvent).detail.row);
                                }else if (appUserRole===AppUserRole.CUSTOMER && delegationType==="received"){
                                    //
                                }else if (appUserRole===AppUserRole.ADMIN || appUserRole===AppUserRole.SALES_PROFILE){
                                    if(delegations[parseInt((event as CustomEvent).detail.row)].documents[0].status === BadgeStatus.APPROVED||delegations[parseInt((event as CustomEvent).detail.row)].documents[0].status === BadgeStatus.NOTAPPROVED){
                                        deleteDelegation((event as CustomEvent).detail.row);
                                    }else{
                                        setRejectionRow(parseInt((event as CustomEvent).detail.row));
                                        setViewRejectionPopup(true);
                                        //rejectDelegation((event as CustomEvent).detail.row);
                                    }
                                }
                            }}
                            onClickKebabAction3={(event:Event)=>{ 
                                if(appUserRole===AppUserRole.CUSTOMER && delegationType==="carried"){
                                    downloadDelegationDocument((event as CustomEvent).detail.row);
                                }else if (appUserRole===AppUserRole.CUSTOMER && delegationType==="received"){
                                    //
                                }else if (appUserRole===AppUserRole.ADMIN || appUserRole===AppUserRole.SALES_PROFILE){
                                    if(delegations[parseInt((event as CustomEvent).detail.row)].documents[0].status === BadgeStatus.APPROVED||delegations[parseInt((event as CustomEvent).detail.row)].documents[0].status === BadgeStatus.NOTAPPROVED){
                                        downloadDelegationDocument((event as CustomEvent).detail.row);
                                    }else{
                                        editDelegation((event as CustomEvent).detail.row);
                                    }
                                }
                            }}
                            onClickKebabAction4={(event:Event)=>{ 
                               if(appUserRole===AppUserRole.CUSTOMER && delegationType==="carried"){
                                    //
                                }else if (appUserRole===AppUserRole.CUSTOMER && delegationType==="received"){
                                    //
                                }else if (appUserRole===AppUserRole.ADMIN || appUserRole===AppUserRole.SALES_PROFILE){
                                    if(delegations[parseInt((event as CustomEvent).detail.row)].documents[0].status === BadgeStatus.APPROVED||delegations[parseInt((event as CustomEvent).detail.row)].documents[0].status === BadgeStatus.NOTAPPROVED){
                                        //
                                    }else{
                                        deleteDelegation((event as CustomEvent).detail.row);
                                    }
                                    
                                }
                                           
                            }}
                            onClickKebabAction5={(event:Event)=>{ 
                              if(appUserRole===AppUserRole.CUSTOMER && delegationType==="carried"){
                                    //
                                }else if (appUserRole===AppUserRole.CUSTOMER && delegationType==="received"){
                                    //
                                }else if (appUserRole===AppUserRole.ADMIN || appUserRole===AppUserRole.SALES_PROFILE){
                                    downloadDelegationDocument((event as CustomEvent).detail.row);
                                }
                            }}
                            onClickKebabAction6={(event:Event)=>{ 
                               if(appUserRole===AppUserRole.CUSTOMER && delegationType==="carried"){
                                    //
                                }else if (appUserRole===AppUserRole.CUSTOMER && delegationType==="received"){
                                    //
                                }else if (appUserRole===AppUserRole.ADMIN || appUserRole===AppUserRole.SALES_PROFILE){
                                    disableEnableDelegation((event as CustomEvent).detail.row);
                                }
                            }}
                        />
                    }
                    {getDelegationLegend(true)}
                </>
            ) : (<>
                 <p
                                    style={{
                                    fontWeight: "bold",
                                    fontSize: "90%",
                                    color:
                                        layoutMode === DsLayout.DARK_MODE
                                        ? "white"
                                        : "#0e2852",
                                }}
                                >
                                {delegationType==='carried'?t("customer.customerDelegationForm.title_3") as string:t("customer.customerDelegationForm.title_4") as string}
                                </p>{" "}
                <p
                    style={{
                        color:
                            layoutMode === DsLayout.DARK_MODE
                                ? "white"
                                : "black",
                    }}
                >
                    {t("transporter.transportData.noDelegationFound")}
                    {/*Non sono presenti deleghe a sistema*/}
                </p></>
            )}
        </div>
        {viewRejectionPopup && (
                <>
                    <DialogMessage
                        title={t(
                            "transporter.transporterData.rejectDialog.delegation.title"
                        )} //{delegationRejectionTitle}
                        subtitle={t(
                            "transporter.transporterData.rejectDialog.delegation.subTitle"
                        )} //{delegationRejectionSubtitle}
                        textareaLabel={t(
                            "drivers.driverData.rejectDialog.textareaLabel"
                        )}
                        textAreaPlaceholder={t(
                            "drivers.driverData.rejectDialog.txtAreaPlaceholder"
                        )}
                        textareaDescription={t(
                            "drivers.driverData.rejectDialog.textareaDescription"
                        )}
                        submitButtonLabel={t(
                            "drivers.driverData.rejectDialog.submitButtonLabel"
                        )}
                        icon={""}
                        type={EntityType.CUSTOMERDELEGATION}
                        onClose={() => setViewRejectionPopup(false)}
                         onClick={rejectDelegation}
                    />
                </>
            )}
    </>
    )
}
export default CustomerDelegationData;

function showPopupError() {
    throw new Error("Function not implemented.");
}
